import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Check, X } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation';

/**
 * Scenarios from the pre-work: the AI's proposal, editable, confirmed by the
 * facilitator. The proposal is the result of the module's action
 * "Ehdota skenaariot ryhmistä" (job ongelmat_v1.propose_scenarios, mode
 * form or place), read from that job; this shows the latest one until it is
 * confirmed or set aside. Nothing is created until "Luo ja sijoita".
 */

const Box = styled.div`
  border-radius: 10px;
  border: 1px solid rgba(74, 158, 255, 0.35);
  background: rgba(74, 158, 255, 0.06);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Button = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$primary ? '#0b0d12' : 'rgba(255,255,255,0.85)')};
  background: ${(p) => (p.$primary ? '#4a9eff' : 'transparent')};
  border: 1px solid ${(p) => (p.$primary ? '#4a9eff' : '#2c3444')};
  &:disabled { opacity: 0.45; cursor: default; }
`;

const Scenario = styled.div`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.input`
  background: transparent;
  color: #fff;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  &:hover, &:focus { border-color: #2c3444; outline: none; background: #0f131b; }
  &:disabled { color: #fff; }
`;

const Description = styled.textarea`
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  font: inherit;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
  min-height: 3.2em;
  &:hover, &:focus { border-color: #2c3444; outline: none; background: #0f131b; }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  span {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.8);
  }
`;

const Muted = styled.div`
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #22c55e;
`;

/**
 * @param {{ clusters: object[], busy: boolean, locked: boolean,
 *   proposalJob: object|null, onCreate: Function }} props
 *   `proposalJob` is the latest ongelmat_v1.propose_scenarios job; a done
 *   one's result carries the proposal.
 */
export default function ScenarioProposal({ clusters, busy, locked, proposalJob, onCreate }) {
  const { t } = useTranslation();
  const [proposal, setProposal] = useState(null);
  // The job whose proposal was confirmed or set aside; it is not shown again.
  const [handledJobId, setHandledJobId] = useState(null);
  const titleOf = (id) => clusters.find((c) => c.id === id)?.title || '?';

  useEffect(() => {
    const r = proposalJob?.status === 'done' ? proposalJob.result : null;
    if (!r?.proposal || proposalJob.id === handledJobId) { setProposal(null); return; }
    setProposal({ proposal: r.proposal, unplaced: r.unplacedIds || [] });
  }, [proposalJob, handledJobId]);

  const edit = (i, patch) => setProposal((p) => ({ ...p, proposal: p.proposal.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const dismiss = () => { setHandledJobId(proposalJob.id); setProposal(null); };
  const create = async () => { await onCreate(proposal.proposal); dismiss(); };

  if (!proposal) {
    return <Muted>{t('preworkCuration.scenarioProposal.fromAction')}</Muted>;
  }

  return (
    <Box>
      <Row>
        <strong>{t('preworkCuration.scenarioProposal.heading', { count: proposal.proposal.length })}</strong>
        <Muted>{t('preworkCuration.scenarioProposal.editHint')}</Muted>
      </Row>
      {proposal.proposal.map((s, i) => (
        <Scenario key={i}>
          <Row>
            <Title value={s.title} onChange={(e) => edit(i, { title: e.target.value })} disabled={!!s.existingScenarioId} placeholder={t('preworkCuration.scenarioProposal.namePlaceholder')} />
            {s.existingScenarioId && <Tag>{t('preworkCuration.scenarioProposal.existingTag')}</Tag>}
          </Row>
          {!s.existingScenarioId && (
            <Description value={s.description || ''} onChange={(e) => edit(i, { description: e.target.value })} placeholder={t('preworkCuration.scenarioProposal.descriptionPlaceholder')} />
          )}
          <Chips>{s.clusterIds.map((id) => <span key={id}>{titleOf(id)}</span>)}</Chips>
          {s.reason && <Muted>{s.reason}</Muted>}
        </Scenario>
      ))}
      {proposal.unplaced?.length > 0 && (
        <Muted>{t('preworkCuration.scenarioProposal.unplaced', { titles: proposal.unplaced.map(titleOf).join(' · ') })}</Muted>
      )}
      <Row>
        <Button $primary onClick={create} disabled={busy || locked || proposal.proposal.some((s) => !s.title.trim())}><Check size={14} /> {t('preworkCuration.scenarioProposal.create')}</Button>
        <Button onClick={dismiss} disabled={busy}><X size={14} /> {t('preworkCuration.scenarioProposal.reject')}</Button>
      </Row>
    </Box>
  );
}
